import React, { useEffect, useMemo, useRef, useState } from "react";
import BrandLogo from "../components/common/BrandLogo";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ChevronRight, MapPin, ExternalLink, ArrowRight, Building2, Compass } from "lucide-react";
import { useSiteSettings } from "../lib/settings";
import { resolveImage } from "../components/admin/ImageUpload";
import BitrixFormEmbed from "../components/common/BitrixFormEmbed";
import Footer from "../components/layout/Footer";
import api from "../lib/api";

const SITE_ORIGIN = "https://firatconstruction.com";

/* Mini-map showing the project's coordinate */
const ProjectMiniMap = ({ lat, lng, label }) => {
  const ref = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const map = L.map(ref.current, {
      center: [lat, lng],
      zoom: 13,
      zoomControl: true,
      scrollWheelZoom: false,
      preferCanvas: true,
    });
    mapRef.current = map;
    L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", {
      subdomains: "abcd",
      maxZoom: 20,
      attribution: '© OpenStreetMap · © CARTO',
    }).addTo(map);
    const html = `
      <div class="yn-project-marker">
        <span class="dot"></span>
        <span class="label">▲ ${label}</span>
      </div>`;
    L.marker([lat, lng], { icon: L.divIcon({ html, className: "yn-poi-icon", iconSize: [14, 14], iconAnchor: [7, 7] }) }).addTo(map);

    return () => { map.remove(); mapRef.current = null; };
  }, [lat, lng, label]);

  return <div ref={ref} className="w-full h-[420px] border border-white/10" data-testid="project-mini-map" />;
};

const YenisehirProjectDetailPage = () => {
  const { slug } = useParams();
  const s = useSiteSettings();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/public/yenisehir-projects/${slug}`)
      .then((r) => setProject(r.data))
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const baseUrl = (s.public_base_url || SITE_ORIGIN).replace(/\/$/, "");
  const canonical = project?.canonical || `${baseUrl}/yenisehir-projeleri/${slug}`;

  const jsonLd = useMemo(() => {
    if (!project) return null;
    const heroAbs = project.og_image || project.hero_image
      ? (project.og_image || project.hero_image).startsWith("http")
        ? (project.og_image || project.hero_image)
        : `${baseUrl}${project.og_image || project.hero_image}`
      : "";
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": project.schema_type || "Place",
          "@id": canonical,
          "name": project.name,
          "description": project.meta_description || project.summary,
          "url": canonical,
          "image": heroAbs || undefined,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": project.district || "Arnavutköy",
            "addressRegion": project.region || "İstanbul",
            "addressCountry": "TR",
          },
          "geo": { "@type": "GeoCoordinates", "latitude": project.lat, "longitude": project.lng },
          "publisher": project.developer ? { "@type": "Organization", "name": project.developer } : undefined,
          "additionalType": project.category,
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Ana Sayfa", "item": `${baseUrl}/` },
            { "@type": "ListItem", "position": 2, "name": "İstanbul Yenişehir", "item": `${baseUrl}/yenisehir` },
            { "@type": "ListItem", "position": 3, "name": "Yenişehirdeki Projeler", "item": `${baseUrl}/yenisehir-projeleri` },
            { "@type": "ListItem", "position": 4, "name": project.name, "item": canonical },
          ],
        },
      ],
    };
  }, [project, baseUrl, canonical]);

  if (notFound) return <Navigate to="/yenisehir-projeleri" replace />;

  if (loading || !project) {
    return (
      <div className="bg-brand-light text-brand-navy min-h-screen flex items-center justify-center" data-testid="yenisehir-detail-loading">
        <div className="text-brand-medium text-sm tracking-[0.28em] uppercase">Yükleniyor…</div>
      </div>
    );
  }

  const heroUrl = resolveImage(project.hero_image);
  const gmapsUrl = project.google_maps_url || `https://www.google.com/maps?q=${project.lat},${project.lng}`;
  const wa = (s.real_estate_whatsapp || s.whatsapp_number || "").replace(/\D/g, "");
  const waMsg = encodeURIComponent(`${project.name} projesi hakkında bilgi almak istiyorum.`);
  const waLink = wa ? `https://wa.me/${wa}?text=${waMsg}` : "";
  const h1 = project.h1 || project.name;
  const metaTitle = project.meta_title || `${project.name} · Yenişehir Projeleri`;

  return (
    <div className="yn-light bg-brand-light text-brand-navy min-h-screen" data-testid={`yenisehir-project-detail-${project.slug}`}>
      <Helmet>
        <html lang="tr" />
        <title>{metaTitle}</title>
        <meta name="description" content={project.meta_description || project.summary} />
        {Array.isArray(project.keywords) && project.keywords.length ? (
          <meta name="keywords" content={project.keywords.join(", ")} />
        ) : null}
        {project.noindex ? <meta name="robots" content="noindex,nofollow" /> : <meta name="robots" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" />}
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={project.meta_description || project.summary} />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content="tr_TR" />
        {(project.og_image || project.hero_image) ? (
          <meta property="og:image" content={
            (project.og_image || project.hero_image).startsWith("http")
              ? (project.og_image || project.hero_image)
              : `${baseUrl}${project.og_image || project.hero_image}`
          } />
        ) : null}
        <meta name="twitter:card" content="summary_large_image" />
        {jsonLd ? <script type="application/ld+json">{JSON.stringify(jsonLd)}</script> : null}
      </Helmet>

      {/* Top bar */}
      <header className="yn-keep-dark border-b border-brand-navy/10 bg-brand-navy backdrop-blur sticky top-0 z-30">
        <div className="mx-auto max-w-[1400px] px-4 md:px-10 h-16 flex items-center justify-between gap-3">
          <Link to="/" aria-label="Ana sayfa" className="flex items-center flex-shrink-0"><BrandLogo place="header" onDark max={40} /></Link>
          <nav className="flex items-center gap-2 md:gap-4 text-[0.6rem] md:text-[0.66rem] tracking-[0.2em] md:tracking-[0.28em] uppercase text-white/80">
            <Link to="/yenisehir-projeleri" className="hover:text-white">Tüm Projeler</Link>
            <Link to="/yenisehir" className="hover:text-white">Rehber</Link>
            {waLink ? <a href={waLink} target="_blank" rel="noreferrer" className="border border-white/40 text-white hover:bg-white hover:text-brand-navy px-3 md:px-4 py-2 transition-colors whitespace-nowrap">WhatsApp</a> : null}
          </nav>
        </div>
      </header>

      {/* Hero — dark cinematic block for impact even on light body */}
      <section className="yn-keep-dark relative overflow-hidden border-b border-brand-navy/10 bg-brand-navy text-white">
        {heroUrl ? (
          <div className="absolute inset-0">
            <img src={heroUrl} alt={h1} className="w-full h-full object-cover opacity-45" />
            <div className="absolute inset-0 bg-gradient-to-b from-brand-navy/40 via-brand-navy/70 to-brand-navy" />
          </div>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-primary to-brand-navy" />
        )}
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-36">
          <nav aria-label="breadcrumb" className="text-xs text-white/60 flex flex-wrap items-center gap-2 mb-8">
            <Link to="/" className="hover:text-white">Ana Sayfa</Link>
            <ChevronRight size={12} />
            <Link to="/yenisehir" className="hover:text-white">Yenişehir</Link>
            <ChevronRight size={12} />
            <Link to="/yenisehir-projeleri" className="hover:text-white">Projeler</Link>
            <ChevronRight size={12} />
            <span className="text-white/90">{project.name}</span>
          </nav>
          {project.hero_kicker ? (
            <div className="text-[0.66rem] tracking-[0.4em] uppercase text-white/70" data-testid="hero-kicker">{project.hero_kicker}</div>
          ) : null}
          <h1 className="mt-6 font-serif leading-[1.05] tracking-tight max-w-4xl text-white" style={{ fontSize: "clamp(2.2rem, 5.5vw, 5rem)" }} data-testid="project-h1">
            {h1}
          </h1>
          {project.summary ? (
            <p className="mt-8 max-w-3xl text-lg text-white/85 leading-relaxed">{project.summary}</p>
          ) : null}
          <div className="mt-10 flex flex-wrap gap-3 text-[0.62rem] tracking-[0.28em] uppercase">
            <span className="border border-white/25 px-3 py-2 text-white/90">{project.category}</span>
            <span className="border border-white/25 px-3 py-2 text-white/90">{project.developer}</span>
            <a href={gmapsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-white/40 text-white hover:bg-white hover:text-brand-navy px-3 py-2 transition-colors">
              <MapPin size={12} /> Google Maps
            </a>
          </div>
        </div>
      </section>

      {/* Overview */}
      {project.overview_body ? (
        <section className="py-20 md:py-28 border-b border-white/5">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-12">
            <div>
              <div className="text-[0.62rem] tracking-[0.32em] uppercase text-brand-gold">Genel Bakış</div>
              <h2 className="mt-4 font-serif text-3xl md:text-4xl leading-tight" data-testid="overview-h2">{project.overview_h2 || "Proje Hakkında"}</h2>
            </div>
            <div className="prose prose-invert max-w-none text-white/75 leading-[1.85]">
              {project.overview_body.split(/\n\n+/).map((para, i) => (
                <p key={i} className="mb-4">{para}</p>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Location */}
      {(project.location_body || (project.location_advantages || []).length > 0 || (Number.isFinite(project.lat) && Number.isFinite(project.lng))) ? (
        <section className="py-20 md:py-28 border-b border-white/5 bg-white/[0.02]">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10">
            <div className="text-[0.62rem] tracking-[0.32em] uppercase text-brand-gold">Konum</div>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl leading-tight max-w-2xl" data-testid="location-h2">{project.location_h2 || "Konum & Ulaşım"}</h2>

            <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <ProjectMiniMap lat={parseFloat(project.lat)} lng={parseFloat(project.lng)} label={project.name} />
                <div className="mt-4 flex items-center justify-between text-xs text-white/50 font-mono tabular-nums">
                  <span>{Number(project.lat).toFixed(6)}, {Number(project.lng).toFixed(6)}</span>
                  <a href={gmapsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-brand-gold hover:text-white">
                    Google Maps&apos;te Aç <ExternalLink size={12} />
                  </a>
                </div>
              </div>
              <div>
                {project.location_body ? (
                  <div className="text-white/75 leading-[1.85] mb-8">
                    {project.location_body.split(/\n\n+/).map((para, i) => (
                      <p key={i} className="mb-4">{para}</p>
                    ))}
                  </div>
                ) : null}
                {(project.location_advantages || []).length > 0 ? (
                  <ul className="space-y-3">
                    {project.location_advantages.map((a, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Compass size={16} className="text-brand-gold mt-1 flex-shrink-0" />
                        <span className="text-white/85">{a}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* Features */}
      {(project.features || []).length > 0 ? (
        <section className="py-20 md:py-28 border-b border-white/5">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10">
            <div className="text-[0.62rem] tracking-[0.32em] uppercase text-brand-gold">Özellikler</div>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl leading-tight max-w-2xl" data-testid="features-h2">{project.features_h2 || "Öne Çıkan Özellikler"}</h2>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {project.features.map((f, i) => (
                <div key={i} className="border border-white/10 hover:border-brand-gold/50 transition-colors p-8 bg-white/[0.02]" data-testid={`feature-${i}`}>
                  <div className="text-3xl mb-4">{f.icon}</div>
                  <h3 className="font-serif text-xl text-white leading-tight">{f.title}</h3>
                  {f.description ? <p className="mt-3 text-sm text-white/65 leading-relaxed">{f.description}</p> : null}
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Gallery */}
      {(project.gallery || []).length > 0 ? (
        <section className="py-20 md:py-28 border-b border-white/5 bg-white/[0.02]">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10">
            <div className="text-[0.62rem] tracking-[0.32em] uppercase text-brand-gold">Görseller</div>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl leading-tight" data-testid="gallery-h2">{project.gallery_h2 || "Galeri"}</h2>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {project.gallery.map((g, i) => (
                <div key={i} className="aspect-[4/3] overflow-hidden">
                  <img src={resolveImage(g)} alt={`${project.name} · ${i + 1}`} className="w-full h-full object-cover hover:scale-[1.05] transition-transform duration-700" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Contact / Lead form */}
      <section id="danis" className="py-20 md:py-28 border-b border-white/5">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="text-[0.62rem] tracking-[0.32em] uppercase text-brand-gold">İletişim</div>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl leading-tight">Bu proje hakkında bilgi almak ister misiniz?</h2>
            <p className="mt-6 text-white/70 leading-relaxed">
              Uzman yatırım danışmanlarımız <strong className="text-white">{project.name}</strong> ve çevresindeki fırsatlar hakkında size özel bir sunum hazırlar.
              Konum analizi, imar durumu, komşu parseller ve değerlenme senaryolarını birlikte inceleyelim.
            </p>
            <div className="mt-8 space-y-2 text-sm text-white/60">
              {s.real_estate_phone || s.contact_phone ? (
                <div>Telefon: <a href={`tel:${s.real_estate_phone || s.contact_phone}`} className="text-brand-gold hover:underline">{s.real_estate_phone || s.contact_phone}</a></div>
              ) : null}
              {waLink ? (
                <div><a href={waLink} target="_blank" rel="noreferrer" className="text-brand-gold hover:underline">WhatsApp ile Ulaş</a></div>
              ) : null}
            </div>
          </div>
          <div className="bg-white p-6 md:p-8">
            <BitrixFormEmbed
              forms={s.bitrix_forms || []}
              formKey="buy"
              extraQuery={`utm_source=yenisehir-projeleri&utm_medium=cms&utm_campaign=${encodeURIComponent(project.slug)}`}
              testId="project-bitrix-form"
            />
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="yn-keep-dark py-16 bg-brand-navy">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="font-serif text-2xl md:text-3xl leading-tight">Diğer Yenişehir Projelerini keşfedin</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/yenisehir-projeleri" className="inline-flex items-center gap-2 bg-brand-gold text-brand-navy px-8 py-4 text-[0.72rem] tracking-[0.3em] uppercase font-medium hover:bg-white transition-colors">
              Tüm Projeler <ArrowRight size={14} />
            </Link>
            <Link to="/yenisehir" className="inline-flex items-center gap-2 border border-white/25 hover:border-brand-gold hover:text-brand-gold text-white px-8 py-4 text-[0.72rem] tracking-[0.3em] uppercase transition-colors">
              <Building2 size={14} /> Yenişehir Rehberi
            </Link>
          </div>
        </div>
      </section>

      <Footer settings={s} />
    </div>
  );
};

export default YenisehirProjectDetailPage;
